//#region Imports
/**
 * To use react related functionality
 */
import React from 'react';
//parse is used to parse the date. addMinutes is used to add minutes in date object
import {parse, addMinutes} from 'date-fns';
//It is used to show Leg Header.
import LegHeader from './LegHeader';
//It is used to show Leg Body.
import LegBody from './LegBody';
//It is used to show Badge.
import TransitBadge from '../TransitBadge'; 
//durationInWords is used to convert durations in seconds in text
//distanceInWords is used to convert distancein meter in text
import { durationInWords, distanceInWords } from '../../../utils/directionsUtilities';
//#endregion

//#region Variable declaration

/**
* It is used to check boardAlightType.
* A "FLAG_STOP" is a type of public transportation service where a vehicle, like a bus or train, stops to pick up or drop off passengers only when requested.
* It means the vehicle only stops at specific locations if a passenger signals the driver by "flagging" them down.
* Flag stops are commonly used in areas with low passenger demand or in rural regions where fixed stops are not available.
* Instead of predetermined stops, the vehicle stops where passengers are waiting or want to disembark.
* This provides flexibility and responsiveness to passenger needs.
*/
const FLAG_STOP = 'FLAG_STOP';

/**
*It is used to check boardAlightType.
*A "DEVIATED_STOP" is a public transportation service where a bus or similar vehicle with 
*a fixed route can make extra stops or detours to cater to passenger requests.
*This allows the vehicle to pick up or drop off passengers at locations slightly off the regular route.
*/
const DEVIATED_STOP = 'DEVIATED';


/**
*It is used to check boardAlightType.
*"MUST_PHONE" in transportation services refers to the mandatory requirement for passengers to contact the 
*transportation provider by phone beforehand to arrange services or request transportation. Passengers are obligated 
*to make a phone call to schedule their trip or inform the service about their specific needs.
*/
const MUST_PHONE = 'mustPhone';
//#endregion

/**
* It is written Directions>>Index.
* Function is used in Directions>>Itinerary and Printview>>TripDetails.
* TripLeg is a single leg of a trip. It represents a single mode of transportation, such as walking, biking, taking public transportation, or driving. 
* @param {Object} 'Leg Details'
* @return {HTMLElement} A Trip Leg
*/
const TripLeg = props => {
    const { idx } = props;
    //if its first leg then add header,body and footer
    if (idx === 0) {
        return renderStartingDirections(props.details);
    }
    //return body and header
    return renderDirections(props.details);
};

/**
* Renders trip detail with header,body and footer.
* Function is used in TripLeg function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Trip leg details
*/
const renderStartingDirections = details => {
    //#region Variable declaration
    //Stores from and to values show from and to name
    const { from, to } = details;
    //It is used to get timezoneDiff
    const today = new Date();
    //It is used to get timezoneDiff
    const agencyOffset = details.agencyTimeZoneOffset / (60 * 1000);
    //It is used to get starttime and endtime
    const timezoneDiff = agencyOffset + today.getTimezoneOffset();
    //Saves start Time
    const startTime = addMinutes(parse(details.startTime), timezoneDiff);
    //It is used to get endtime
    const toTime = to.departure ? to.departure : to.arrival;
    //Saves end time
    const endTime = addMinutes(parse(toTime), timezoneDiff);
    //#endregion

    return(
        <>
            <LegHeader
                time={startTime}
                heading={from.name}
            />
            <LegBody
                details={details}
                text={getTripText(details)}
            />
            <LegHeader
                time={endTime}
                heading={to.name}
            />
        </>
    );
};


/**
* Renders trip details with body and header.
* Function is used in TripLeg function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Trip leg details
*/
const renderDirections = details => {
    //#region Variable declaration
    //Stores from and to values show from and to name
    const { from, to } = details;
    //It is used to get timezoneDiff
    const today = new Date();
    //It is used to get timezoneDiff
    const agencyOffset = details.agencyTimeZoneOffset / (60 * 1000);
    //It is used to get starttime and endtime
    const timezoneDiff = agencyOffset + today.getTimezoneOffset();
    //It is used to get endtime
    const toTime = to.departure ? to.departure : to.arrival;
    //Saves end time
    const endTime = addMinutes(parse(toTime), timezoneDiff);
    //#endregion

    // if stopId object does not contain 'area' and from, to stopId is same then return null
    if (!(from.stopId.includes('area') || !from.stopId.includes('area')) && from.stopId === to.stopId) {
        return null;
    }
    return (
        <>
            <LegBody
                details={details}
                text={getTripText(details)}
            />
            <LegHeader
                time={endTime}
                heading={to.name}
            />
        </>
    );
};

/**
* The code is used to determine trip Text based on the value of the property mode of the object passed as an argument.
* Function is used in renderStartingDirections and renderDirections function from the same component.
* @param {String} 'Trip Detail'
* @return {String} triptext
*/
const getTripText = details => {
    if (details.mode === 'WALK') {
        return getWalkingTripText(details);
    } else {
        return getTransitTripText(details);
    }
};


/**
* Return walking distance and duration in words.
* Function is used in getTripText function from the same component.
* @param {String} 'Trip Detail'
* @return {String} Walking Text
*/
const getWalkingTripText = details => {
    const { distance, duration } = details;
    return (
        <>
            Walk {distanceInWords(distance)} ({durationInWords(duration)})
        </>
    );
};

/**
* Return walking distance and duration in words.
* Function is used in getTripText function from the same component.
* @param {String} 'Trip Detail' 
* @return {HTMLElement} Leg Details
*/
const getTransitTripText = details => {
    //The "agencyUrl" property represents the URL or web address of the transit agency's website. It provides a link to the official website of the agency associated with the transit data used in OTP. 
    //The "agencyName" property represents the name or title of the transit agency that operates the transportation services included in the OTP system. It typically provides the official name or branding of the agency responsible for the transit data used in OTP.
    //The "headsign" property in OpenTripPlanner refers to the destination or end point displayed on the front or side of a transit vehicle. It helps passengers identify the direction the vehicle is traveling and ensures they board the correct vehicle for their intended destination.
    //Setting 'interlineWithPreviousLeg' to true in OTP enables seamless transfers between different transportation modes, allowing users to directly transfer between consecutive legs of a trip without needing to exit a station or stop and physically walk to another vehicle or station.
    const { agencyUrl, agencyName, headsign, interlineWithPreviousLeg } = details;
    // The code snippet assigns the appropriate value to the boardingAction variable based on the condition interlineWithPreviousLeg, indicating whether the user should "Continue on" without exiting the station or "Board" a new vehicle.
    const boardingAction = interlineWithPreviousLeg ? 'Continue on' : 'Board';
    return (
        <>
            {getFromFlexText(details)}
            {getFromFlex2Text(details)}
            {getFromFlex2AltText(details)}
            <div className="otp-boarding-details">
                {boardingAction} <TransitBadge leg={details} />{headsign && <span className="otp-headsign">toward {headsign}</span>}
            </div>
            <div className="otp-service-info">
                Service provided by <a href={agencyUrl}>{agencyName}</a>
            </div>
            {getToFlexText(details)}
            {getToFlex2Text(details)}
            {getToFlex2AltText(details)}
        </>
    );
};

/**
* Return Pickup message.
* Function is used in getTransitTripText function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Pickup Message
*/
const getFromFlexText = details => {
    //stores from property detail
    const { from } = details;
    if(from.boardAlightType){
        if (from.boardAlightType === FLAG_STOP) {
            return (
                <div className="otp-flex-message otp-pickup-message">
                    {details.flexFlagStopPickupMessage}
                </div>
            );
        }
        if (from.boardAlightType === DEVIATED_STOP) {
            return (
                <div className="otp-flex-message otp-pickup-message">
                    {details.flexDrtPickupMessage}             
                    
                </div>
            );
        }
    }

    return '';
};

/**
* Return Pickup message when board rule is MUST_PHONE.
* Function is used in getTransitTripText function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Pickup Message
*/
const getFromFlex2Text = details => {
    if (window.otpConfig.flex2) {
        if (details.boardRule === MUST_PHONE) {
            return (
                <div className="otp-flex-message otp-pickup-message">                 
                </div>
            );
        }
        return '';
    }
};

/**
* Return Pickup message.
* Function is used in getTransitTripText function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Pickup Message
*/
const getFromFlex2AltText = details => {
    if (window.otpConfig.flex2) {
        if (details.pickupBookingInfo != null) {
            return (
                <div className="otp-flex-message otp-pickup-message">                 
                </div>
            );
        }
        return '';
    }
};

/**
* Return dropoff message if boardAlightType is FLAG_STOP or DEVIATED_STOP. 
* Function is used in getTransitTripText function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Dropoff Message
*/
const getToFlexText = details => {
    //Stores to and callAndRide values from leg details
    //The "callAndRide" property indicates if a transit service is a flexible, on-demand "call and ride" service.
    //It allows passengers to request a transit vehicle to pick them up from a specific location and take them to their destination.
    //OTP includes this property to recognize and integrate these services into the trip planning system, providing users with additional flexible transportation options beyond traditional fixed routes and schedules.
    const { to, callAndRide } = details;
    if(to.boardAlightType)
    {
        if (to.boardAlightType === FLAG_STOP) {
            return (
                <div className="otp-flex-message otp-dropoff-message">
                    {details.flexFlagStopDropOffMessage}
                </div>
            );
        }
        if (to.boardAlightType === DEVIATED_STOP) {
            if (callAndRide && callAndRide === true) {
                return '';
            }
            return (
                <div className="otp-flex-message otp-dropoff-message">
                    {details.flexDrtDropOffMessage}
                </div>
            );
        }
    }

    return '';
};

/**
* Return dropoff message if boardRule is MUST_PHONE. 
* Function is used in getTransitTripText function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Dropoff Message
*/
const getToFlex2Text = details => {
    if (window.otpConfig.flex2) {
        if (details.boardRule === MUST_PHONE) {
            return (
                <div className="otp-flex-message otp-dropoff-message">
                    {details.dropOffBookingInfo.message}                   
                </div>
            );
        }
        return '';
    }
};

/**
* Return dropoff message if dropOffBookingInfo is not null. 
* Function is used in getTransitTripText function from the same component.
* @param {String} 'Trip Detail'
* @return {HTMLElement} Dropoff Message
*/
const getToFlex2AltText = details => {
    if (window.otpConfig.flex2) {
        if (details.dropOffBookingInfo != null) {
            return (
                <div className="otp-flex-message otp-dropoff-message">
                    {details.dropOffBookingInfo.dropOffMessage}
                </div>
            );
        }
        return '';
    }
};
export default TripLeg;